/**
 * Sidebar — collapsible icon+label navigation.
 *
 * H13: Expand/collapse toggle persisted to localStorage.
 * - Collapsed (default): 68px icon-only with hover tooltips.
 * - Expanded: 220px with labels + brand name.
 */

import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import {
  TrendingUp,
  Target,
  Users,
  BookOpen,
  LogOut,
  Bell,
  MessageCircle,
  Filter,
  Link2,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Settings,
  CreditCard,
  Rocket,
  Smartphone,
  Mail,
  ExternalLink,
  BadgeCheck,
  UserPlus,
  AtSign,
  Home,
  Layers,
  Plus,
  Percent,
  Palette,
  TicketPercent,
  BarChart3,
} from "lucide-react";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useIntegracion } from "../../hooks/useIntegracion";
import { useAuth } from "../../hooks/useAuth";
import { whatsappAccountsApi } from "../../modules/whatsapp/api";
import { activaApi } from "../../modules/campana-nueva/api";
import { useRolActiva } from "../../modules/campana-nueva/hooks/useEsEquipoDeenex";
import { R_OFICIAL, seccionesOficialPara, useRevisarConfiguracion } from "../../modules/wa-oficial/components/SeccionesOficial";
import { seccionesEmailsPara } from "../../modules/emails/SeccionesEmails";
import { R_EMAILS } from "../../modules/emails/logic/emails";
import { R_PUBLICO } from "../../modules/publico/logic/publico";
import { R_INICIO } from "../../modules/inicio/logic/inicio";
import { R_SEGMENTACIONES } from "../../modules/segmentaciones/logic/segmentar";
import { R_CAMPANAS, R_NUEVA } from "../../modules/campanas/logic/campanas";
import { R_ESTADISTICAS } from "../../modules/envios/logic/estadisticas";
import { R_DESCUENTOS } from "../../modules/descuentos/logic/descuentos";
import { R_BRANDING } from "../../modules/branding/logic/branding";
import { R_CUPONES } from "../../modules/cupones/logic/cupones";
import { useBaseVieja } from "../../modules/base-ventas/hooks/useBaseVieja";
import SelectorMarca from "../../modules/campana-nueva/components/SelectorMarca";
import { ContadorSinResponder } from "../../modules/wa-oficial/components/conversaciones/ContadorSinResponder";
import logoPrimary from "../../assets/logo-primary.png";
// SOLO_ACTIVA vive en config/producto: con él el menú muestra sólo DEENEX Activa (ver `nav`).
import { SOLO_ACTIVA, HOME, NOMBRE_PRODUCTO } from "../../config/producto";

const SIDEBAR_KEY = "deenex:sidebar:expanded";
/** Las secciones del menú que se abrieron a mano con la flecha (ver `abiertasAMano`). */
const SECCIONES_KEY = "deenex:sidebar:secciones-abiertas";

interface SubItem {
  path: string;
  label: string;
  title: string;
  icon: ReactNode;
  /** Por defecto `true`. `false` para los que tienen subrutas y tienen que seguir pintados adentro. */
  end?: boolean;
  /** Cuándo va pintado, si no alcanza con la ruta del link. */
  activo?: (pathname: string) => boolean;
  aviso?: "por-contestar" | "revisar-config";
  /**
   * Se abre en otra pestaña. «Ver como cliente» tenía el ícono de link externo pero abría `/cliente` en la misma,
   * una página sin menú ni forma de volver: en el celular quedabas atrapado (QA del 08/10). En otra pestaña,
   * volver es cerrarla, como promete el ícono.
   */
  nuevaPestana?: boolean;
}

/** Un grupo adentro de una sección (WhatsApp → «Oficial (API)» y «QR (líneas)»), con su propio acordeón. */
interface GrupoNav {
  id: string;
  label: string;
  /** ¿Estás adentro del grupo? Ése va abierto siempre. */
  activo: (pathname: string) => boolean;
  sub: SubItem[];
}

interface NavItem {
  id: string;
  path: string;
  icon: ReactNode;
  label: string;
  warning?: boolean;
  /** Lo que dice el lector de pantalla del punto de `warning`: el link tiene aria-label y el punto solo no se lee. */
  textoAviso?: string;
  /** Un título arriba del ítem: el «Canales» del menú nuevo, sobre WhatsApp y Emails. */
  titulo?: string;
  /** Cuándo va pintado el ítem. Sin esto manda el NavLink, que pinta por prefijo: «/activa» quedaría pintado en todo. */
  activo?: (pathname: string) => boolean;
  /** Todos los sub-ítems de la sección (con grupos, la suma de los de cada grupo: de acá salen los avisos). */
  sub?: SubItem[];
  /** Con grupos, la sección se dibuja por grupo, cada uno plegable (`grupoAbierto`). */
  grupos?: GrupoNav[];
}

/**
 * Las pantallas de la reactivación por archivo: la lista (/activa), el asistente, las líneas, las casillas y el detalle
 * de una campaña (/activa/campanas/:id). /activa/campanas a secas es la lista de TODAS las campañas y
 * /activa/campanas/nueva, elegir qué campaña armar: no son de QR.
 */
const RUTAS_REACTIVACION = /^\/activa(\/(real|whatsapp|email)(\/.*)?|\/campanas\/.+)?\/?$/;
const enRuta = (p: string, r: string) => p === r || p.startsWith(`${r}/`);
const esReactivacion = (p: string) => RUTAS_REACTIVACION.test(p) && !enRuta(p.replace(/\/+$/, ""), R_NUEVA);
const esOficial = (p: string) => p === R_OFICIAL || p.startsWith(`${R_OFICIAL}/`);
/**
 * «Campañas» (la lista única, pedido de Alan del 28/10/2026) es la única entrada del menú a las campañas: va pintada en
 * ella y en la lista y el detalle de cada mundo (el oficial, con segmentación, por QR y de mail), a los que se llega
 * desde sus filtros.
 */
const esCampanas = (p: string) =>
  p === R_CAMPANAS ||
  p === "/activa" ||
  p === "/activa/real" ||
  p.startsWith(`${R_CAMPANAS}/`) ||
  enRuta(p, R_SEGMENTACIONES) ||
  enRuta(p, `${R_OFICIAL}/campanas`) ||
  enRuta(p, `${R_EMAILS}/campanas`);

/**
 * Iniciales del comercio para el avatar. nombreActual() cae al brandId cuando no se guardó el
 * nombre: un ObjectId (o cualquier cosa sin letras) daría un avatar ilegible, así que ahí va «DX».
 */
function iniciales(nombre: string): string {
  const limpio = nombre.trim();
  if (!limpio || /^[0-9a-f]{24}$/i.test(limpio) || !/\p{L}/u.test(limpio)) return "DX";
  const palabras = limpio.split(/\s+/).filter((p) => /\p{L}/u.test(p));
  const letras = palabras.length > 1 ? palabras[0][0] + palabras[1][0] : palabras[0].slice(0, 2);
  return letras.toUpperCase();
}

interface SidebarProps {
  /** Controla si el drawer está abierto en mobile (md:hidden) */
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

const Sidebar = ({ mobileOpen = false, onMobileClose }: SidebarProps) => {
  const { marca, getMarca } = useIntegracion();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [waAccountsCount, setWaAccountsCount] = useState<number | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [expanded, setExpanded] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_KEY);
      // Primera visita (sin preferencia guardada): expandida por default
      return stored === null ? true : stored === "true";
    } catch {
      return true;
    }
  });

  /**
   * Las secciones que se abrieron a mano (con la flecha) sin estar adentro. La sección donde estás va abierta
   * siempre; las demás, cerradas, salvo que se abran acá. Se recuerda en este navegador: es una comodidad, y si
   * el almacenamiento no anda el menú funciona igual (arranca con todo cerrado menos la sección actual).
   */
  const [abiertasAMano, setAbiertasAMano] = useState<string[]>(() => {
    try {
      const v = JSON.parse(localStorage.getItem(SECCIONES_KEY) || "[]");
      return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
    } catch {
      return [];
    }
  });
  const alternarSeccion = (id: string) => {
    setAbiertasAMano((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      try {
        localStorage.setItem(SECCIONES_KEY, JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  const toggle = () => {
    setExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      navigate("/login", { replace: true });
    }
  };

  useEffect(() => {
    // /integracion/marca es la identidad de la plataforma anterior. Con SOLO_ACTIVA el Sidebar
    // muestra el comercio con el que trabaja Activa (nombreActual), así que no se pide.
    if (SOLO_ACTIVA) return;
    if (!marca) getMarca();
  }, [marca, getMarca]);

  useEffect(() => {
    // /whatsapp/accounts es del módulo legacy: con SOLO_ACTIVA el ítem no se muestra y el
    // backend ni lo monta, así que el pedido sólo sumaría un 404 en cada carga.
    if (SOLO_ACTIVA) return;
    whatsappAccountsApi.list().then((r) => {
      if (r.success) setWaAccountsCount(r.data.length);
    });
  }, []);

  const showWaWarning = waAccountsCount === 0;

  /**
   * Con SOLO_ACTIVA la identidad es la del comercio activo de Activa, la misma que muestra el
   * selector del header: el operador alterna entre comercios reales y el Sidebar no puede decir
   * otra cosa. Se lee en cada render sin estado: cambiar de comercio recarga la página.
   */
  const comercio = SOLO_ACTIVA ? activaApi.nombreActual() : null;

  /**
   * Una persona de un cliente (Accesos, rol «comercio», según el canje) no ve lo del equipo: las líneas
   * y las casillas (las conecta Deenex), la lista de Accesos ni la Configuración del WhatsApp (en su lugar,
   * «Saldo»: `seccionesOficialPara`). Bajo su marca dice «Tu marca», no «Operador».
   */
  const rol = useRolActiva();
  const equipo = rol === "equipo";
  /** El punto de «base desactualizada» al lado de Público: la base de ventas es del equipo (no se pide para un cliente). */
  const baseVieja = useBaseVieja(SOLO_ACTIVA && equipo);
  const VER_COMO_CLIENTE = { path: "/cliente", label: "Ver como cliente", title: "Ver como cliente (se abre en otra pestaña)", icon: <ExternalLink size={15} />, nuevaPestana: true };

  const primaryNav: NavItem[] = [
    {
      id: "dashboard",
      path: "/dashboard",
      icon: <TrendingUp size={20} />,
      label: "Dashboard",
    },
    {
      id: "activa",
      path: "/activa",
      icon: <Rocket size={20} />,
      label: "DEENEX Activa",
      /**
       * Sub-navegación de la sección. Estos accesos vivían como tarjetas al pie de /activa:
       * para conectar una casilla desde el centro de control había que volver a la lista y
       * bajar hasta el final. Acá están siempre a un click, y se ven sólo cuando estás en la
       * sección (en el resto del producto serían ruido).
       */
      sub: [
        // Labels cortos a propósito: "Conectar WhatsApp" no entra (124px de texto en 117px de
        // ancho) y se cortaba. El verbo sobra —ya estás en la sección— y "WhatsApp" a secas
        // chocaría con el ítem WhatsApp del menú principal, que es otro módulo. "Líneas" y
        // "Casillas" son los objetos que se administran en cada canal, y es el mismo vocabulario
        // que usa la pantalla ("Conectar casilla"). El title completa el sentido al pasar el mouse.
        ...(equipo ? [{ path: "/activa/whatsapp", label: "Líneas", title: "Líneas de WhatsApp", icon: <Smartphone size={15} /> }] : []),
        // «WhatsApp oficial» (16 caracteres) está al borde de los 117 px, con `truncate` puesto.
        // Si en el navegador se corta, pasa a «WA oficial» (el title completa el sentido).
        // `end: false`: la sección tiene subrutas (/plantillas, /campanas…) y el ítem tiene que
        // seguir pintado adentro de ellas; con `end` fijo se apagaba fuera de /activa/oficial exacto.
        { path: "/activa/oficial", label: "WhatsApp oficial", title: "WhatsApp oficial (API de Meta vía Twilio)", icon: <BadgeCheck size={15} />, end: false },
        ...(equipo ? [{ path: "/activa/email", label: "Casillas", title: "Casillas de email", icon: <Mail size={15} /> }] : []),
        // Quién puede entrar por mail: sólo con una sesión de persona (su `user` trae `acceso`) y del
        // equipo. Una sesión guardada antes de Accesos no lo trae: el link aparece en el próximo login.
        ...(user?.acceso && equipo
          ? [{ path: "/activa/accesos", label: "Accesos", title: "Quién puede entrar", icon: <UserPlus size={15} /> }]
          : []),
        // «Ver como cliente» es para el equipo: el cliente ya es el cliente (ver `navActiva`)
        ...(equipo ? [VER_COMO_CLIENTE] : []),
      ],
    },
    {
      id: "objectives",
      path: "/objectives",
      icon: <Target size={20} />,
      label: "Campañas",
    },
    {
      id: "audience",
      path: "/audience",
      icon: <Users size={20} />,
      label: "Audiencia",
    },
    {
      id: "segments",
      path: "/segments",
      icon: <Filter size={20} />,
      label: "Segmentos",
    },
    {
      id: "brand_rules",
      path: "/brand",
      icon: <BookOpen size={20} />,
      label: "Manual de tu IA",
    },
    {
      id: "notifications",
      path: "/notifications",
      icon: <Bell size={20} />,
      label: "Push",
    },
    {
      id: "whatsapp",
      path: "/whatsapp",
      icon: <MessageCircle size={20} />,
      label: "WhatsApp",
      warning: showWaWarning,
    },
    {
      id: "integrations",
      path: "/integrations",
      icon: <Link2 size={20} />,
      label: "Integraciones",
    },
    {
      id: "wallet",
      path: "/wallet",
      icon: <CreditCard size={20} />,
      label: "Plan & Créditos",
    },
  ];

  /**
   * Menú acotado a DEENEX Activa: el resto del producto (Dashboard, Campañas, Audiencia, Manual
   * de tu IA, Push, WhatsApp, Integraciones, Plan & Créditos) no se muestra, para que la app se
   * lea como UNA herramienta y no como un módulo perdido adentro de otra cosa. La misma bandera
   * oculta «Configuración» en la sección de abajo (ver más abajo). Volver al menú completo es poner
   * `SOLO_ACTIVA = false`: `primaryNav` sigue entero.
   *
   * Hasta el 07/10/2026 había un solo ítem, «Principal» (la lista de campañas por archivo), y WhatsApp
   * oficial era uno de sus sub-ítems, con 8 pestañas adentro de la página: para pasar de Campañas a
   * Estadísticas había que entrar y buscar la pestaña. Pedido de Alan: que el menú diga «WhatsApp» y
   * abajo cada sección. Son tres secciones:
   * - **WhatsApp**: el canal oficial, primero porque es el que se opera todos los días. Sus sub-ítems
   *   son las secciones que antes eran pestañas (`SECCIONES_OFICIAL`), con sus avisos.
   * - **Emails** (desde el 10/10/2026): las campañas de mail de Snov del comercio (`SECCIONES_EMAILS`).
   * - **Reactivación**: lo que era «Principal», las campañas por archivo (WhatsApp por línea y email)
   *   con sus líneas y casillas. La lista de bajas es una sola, en WhatsApp → Bajas.
   * «Accesos» no es de ningún canal: va abajo, junto a «Cerrar sesión».
   *
   * Desde la fase 1 de DEENEX Activa (13/10/2026), el equipo ve el menú nuevo del diseño (docs/activa/DISENO.md, «Mapa»):
   * Inicio · Público · Contexto · Campañas · Estadísticas y, bajo el título «Canales», WhatsApp y Emails intactos (con sus
   * submenús), seguidos de Branding y Descuentos. Cada ítem aparece cuando su pantalla existe, y una persona de un
   * cliente sigue con el menú de antes hasta que esté completo (mergear es desplegar: un ítem sin pantalla lo vería el
   * comercio). Fase 0, dudas 1 y 2. El diseño decía «Configuración», pero WhatsApp y Emails tienen campañas y
   * conversaciones, y adentro otra «Configuración»; y el Inicio de WhatsApp se llama «Resumen» para el equipo, que ya
   * tiene el de Activa (`SeccionesOficial`). Decisión de Alan del 13/10/2026.
   */
  // sin «Campañas»: las del oficial están en la lista única (y su propia lista, en su filtro)
  const subOficial: SubItem[] = seccionesOficialPara(equipo).filter((s) => s.path !== `${R_OFICIAL}/campanas`).map((s) => ({
    path: s.path,
    label: s.label,
    title: s.title,
    icon: <s.icono size={15} />,
    end: s.end,
    aviso: s.aviso,
  }));
  /**
   * WhatsApp → QR (líneas): lo que era la sección «Reactivación», las líneas y las casillas de las campañas por archivo.
   * Sus campañas están en la lista única (y su propia lista, /activa, en su filtro). A una persona de un cliente no le
   * queda nada en este grupo (las líneas y las casillas son del equipo): no se dibuja.
   */
  const subQr: SubItem[] = [
    ...(equipo
      ? [
          { path: "/activa/whatsapp", label: "Líneas", title: "Líneas de WhatsApp (QR)", icon: <Smartphone size={15} /> },
          // las campañas por archivo también salen por mail, desde estas casillas
          { path: "/activa/email", label: "Casillas de mail", title: "Casillas de mail de las campañas por QR", icon: <Mail size={15} /> },
        ]
      : []),
    // Sin «No-contactar»: es la misma lista que WhatsApp → Bajas (una baja por cualquier canal frena todo).
    // Tener dos entradas para una lista hacía pensar que eran dos. /activa/no-contactar redirige a Bajas.
    // «Ver como cliente» es para el equipo, que quiere ver lo que ve el comercio. A una persona del cliente se le
    // mostraba igual (QA del 08/10): para ella es su propio panel visto «como cliente», un link que no tiene sentido.
    ...(equipo ? [VER_COMO_CLIENTE] : []),
  ];

  const navActiva: NavItem[] = [
    // Inicio (DISENO §1): del equipo, como el resto del menú nuevo.
    ...(equipo ? [{ id: "inicio", path: R_INICIO, icon: <Home size={20} />, label: "Inicio", activo: (p: string) => p === R_INICIO }] : []),
    {
      // Público: una fila por persona de todos los canales, con su segmento y lo que hizo. Pedido de Alan del 12/10/2026.
      id: "publico",
      path: R_PUBLICO,
      icon: <Users size={20} />,
      label: "Público",
      activo: (p) => p === R_PUBLICO || p.startsWith(`${R_PUBLICO}/`),
      // la base de ventas en uso pasó el umbral de antigüedad (DISENO, «Mapa»: «Público (base desactualizada)»)
      warning: baseVieja,
      textoAviso: "(base desactualizada)",
    },
    // Campañas: UNA sola vez en el menú (28/10/2026), la lista de todas las del comercio. La ven el equipo y el cliente
    // (al cliente, sin las con segmentación, que son del equipo).
    { id: "campanas", path: R_CAMPANAS, icon: <Layers size={20} />, label: "Campañas", activo: esCampanas },
    // Estadísticas (fase 5c): lo que vendieron de más las campañas, sumado por período. Del equipo, como Campañas.
    ...(equipo ? [{ id: "estadisticas", path: R_ESTADISTICAS, icon: <BarChart3 size={20} />, label: "Estadísticas", activo: (p: string) => p === R_ESTADISTICAS }] : []),
    {
      // WhatsApp, con sus dos vías (pedido de Alan del 12/10/2026: «Reactivación era básicamente el WhatsApp con QR»):
      // «Oficial (API)», el canal de Twilio y Meta, y «QR (líneas)», las campañas por archivo que salen por las líneas
      // vinculadas escaneando el QR (antes, la sección «Reactivación»). Cada grupo se pliega como las secciones.
      id: "whatsapp",
      path: R_OFICIAL,
      icon: <MessageCircle size={20} />,
      label: "WhatsApp",
      titulo: equipo ? "Canales" : undefined,
      // a una persona de un cliente no le queda nada en «QR (líneas)»: la lista y el detalle por QR no le abren WhatsApp
      activo: (p) => esOficial(p) || (subQr.length > 0 && esReactivacion(p)),
      sub: [...subOficial, ...subQr],
      grupos: [
        { id: "oficial", label: "Oficial (API)", activo: esOficial, sub: subOficial },
        { id: "qr", label: "QR (líneas)", activo: esReactivacion, sub: subQr },
      ].filter((g) => g.sub.length > 0),
    },
    {
      // Emails: las campañas de Snov del comercio (estadísticas, personas y respuestas). Pedido de Alan del 10/10/2026.
      id: "emails",
      path: R_EMAILS,
      icon: <AtSign size={20} />,
      label: "Emails",
      activo: (p) => p === R_EMAILS || p.startsWith(`${R_EMAILS}/`),
      // sin «Campañas»: las de mail están en la lista única (y su propia lista, en su filtro)
      sub: seccionesEmailsPara(equipo).filter((s) => s.path !== `${R_EMAILS}/campanas`).map((s) => ({
        path: s.path,
        label: s.label,
        title: s.title,
        icon: <s.icono size={15} />,
        end: s.end,
      })),
    },
    // Tu marca (fase 3): Branding y Descuentos, y desde la fase 4 Cupones, abajo de los canales. El diseño los ponía bajo «Configuración»; Alan pidió
    // «Tu marca» (14/10), porque WhatsApp tiene su propia «Configuración» adentro y el pie del menú otra. Del equipo.
    ...(equipo
      ? [
          { id: "branding", path: R_BRANDING, icon: <Palette size={20} />, label: "Branding", titulo: "Tu marca", activo: (p: string) => p === R_BRANDING },
          { id: "descuentos", path: R_DESCUENTOS, icon: <Percent size={20} />, label: "Descuentos", activo: (p: string) => p === R_DESCUENTOS },
          { id: "cupones", path: R_CUPONES, icon: <TicketPercent size={20} />, label: "Cupones", activo: (p: string) => p === R_CUPONES },
        ]
      : []),
  ];

  const nav = SOLO_ACTIVA ? navActiva : primaryNav;
  /** ¿Estás adentro de esta sección? (la misma cuenta que pinta el título) */
  const estaAdentro = (item: NavItem) => (item.activo ? item.activo(pathname) : pathname.startsWith(item.path));
  /**
   * ¿Se ven los sub-ítems? Con el menú acotado a Activa eran SIEMPRE visibles (es el único menú que hay y esconderlo
   * obligaba a entrar para ver las partes), pero con Público, WhatsApp, Emails y Reactivación abiertas a la vez el
   * menú no entraba en la pantalla y lo de abajo había que buscarlo con el scroll (Alan, 12/10: en Emails sólo se
   * veía «Resumen»). Ahora es un acordeón: la sección donde estás, abierta; las demás, con una flecha para abrirlas
   * sin navegar. En el menú completo sigue como antes: sólo adentro de la sección.
   */
  const seccionAbierta = (item: NavItem) => (SOLO_ACTIVA ? estaAdentro(item) || abiertasAMano.includes(item.id) : pathname.startsWith(item.path));
  const revisarConfig = useRevisarConfiguracion();

  // En mobile siempre mostramos la sidebar expandida (con labels) cuando está abierta
  const effectiveExpanded = mobileOpen ? true : expanded;

  /** Lo mismo adentro de una sección con grupos: el grupo donde estás, abierto; los demás, con su flecha. */
  const claveGrupo = (item: NavItem, g: GrupoNav) => `${item.id}:${g.id}`;
  const grupoAbierto = (item: NavItem, g: GrupoNav) => g.activo(pathname) || abiertasAMano.includes(claveGrupo(item, g));
  /** Los avisos de unos sub-ítems que no se ven (sección o grupo cerrados): suben al título, para no esconderlos. */
  const avisosDe = (subs: SubItem[]) => ({
    contestar: subs.some((x) => x.aviso === "por-contestar"),
    revisar: !!revisarConfig && subs.some((x) => x.aviso === "revisar-config"),
  });
  const avisos = (a: { contestar: boolean; revisar: boolean }) => (
    <>
      {a.contestar && <ContadorSinResponder compacto={!effectiveExpanded} />}
      {a.revisar && (
        <>
          <span
            className={`rounded-full bg-violet-500 flex-shrink-0 ${effectiveExpanded ? "w-1.5 h-1.5" : "absolute top-0.5 right-0.5 w-2 h-2 ring-2 ring-white"}`}
            title={TITULO_REVISAR}
            aria-hidden="true"
          />
          <span className="sr-only">(hay algo para revisar)</span>
        </>
      )}
    </>
  );
  /** Un sub-ítem del menú (lo usan las secciones con y sin grupos). */
  const subItem = (s: SubItem) => (
    <NavLink
      key={s.path}
      to={s.path}
      end={s.end !== false}
      // Sin aria-label: el nombre sale del contenido (el label, o el title colapsado) y así incluye el
      // aviso para el lector de pantalla («Conversaciones (3 por contestar)»). Un aria-label lo tapaba.
      title={s.title}
      target={s.nuevaPestana ? "_blank" : undefined}
      rel={s.nuevaPestana ? "noopener noreferrer" : undefined}
      onClick={() => onMobileClose?.()}
      className={({ isActive }) => `
        ${effectiveExpanded ? "w-full px-2 gap-2" : "w-8 justify-center"}
        h-8 rounded-lg flex items-center transition-all duration-200 relative group
        ${(s.activo ? s.activo(pathname) : isActive) ? "text-indigo-600 bg-indigo-50/70 font-semibold" : "text-slate-400 hover:text-indigo-600 hover:bg-slate-50"}
      `}
    >
      <span className="flex-shrink-0">{s.icon}</span>
      {effectiveExpanded ? (
        <span className="text-[13px] truncate">{s.label}</span>
      ) : (
        <>
          <span className="sr-only">{s.title}</span>
          {/* colapsado el label no entra: el tooltip es el único modo de saber qué es */}
          <span
            className="pointer-events-none absolute left-full ml-3 px-2.5 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-medium whitespace-nowrap shadow-lg opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all duration-200 z-50"
            aria-hidden="true"
          >
            {s.title}
          </span>
        </>
      )}
      {/* Un solo contador, también colapsado (`compacto`): expandir el menú no lo desmonta. */}
      {s.aviso === "por-contestar" && <ContadorSinResponder compacto={!effectiveExpanded} />}
      {s.aviso === "revisar-config" && revisarConfig && (
        <>
          <span
            className={`w-1.5 h-1.5 rounded-full bg-violet-500 flex-shrink-0 ${
              effectiveExpanded ? "ml-auto" : "absolute top-0.5 right-0.5 w-2 h-2 ring-2 ring-white"
            }`}
            title={TITULO_REVISAR}
            aria-hidden="true"
          />
          <span className="sr-only">(hay algo para revisar)</span>
        </>
      )}
    </NavLink>
  );

  return (
    <>
      {/* Overlay para cerrar el drawer en mobile */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-20 md:hidden"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}
    <aside
      className={`
        /* Mobile: drawer deslizante desde la izquierda */
        fixed inset-y-0 left-0 z-30
        transition-transform duration-300 ease-in-out
        ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
        /* Desktop: en flujo normal, siempre visible */
        md:relative md:translate-x-0 md:z-20
        /* Ancho según estado expanded */
        ${effectiveExpanded ? "w-[220px]" : "w-[68px]"}
        h-screen flex flex-col justify-between py-6
        ${effectiveExpanded ? "px-3" : "px-2"}
        bg-white/80 backdrop-blur-md border-r border-slate-200/50
        transition-[width,padding,transform] duration-300 ease-in-out
        shadow-[0_0_20px_rgba(0,0,0,0.03)] overflow-hidden
      `}
      style={{
        borderTopRightRadius: "24px",
        borderBottomRightRadius: "24px",
      }}
    >
      {/* ── Top section ──
          Con los dos grupos de WhatsApp abiertos el menú tiene 15 ítems y no entra en una pantalla de 720 px de alto:
          la lista scrollea y la sección de abajo (comercio, Accesos, Cerrar sesión) queda siempre a la vista. */}
      <div className="flex flex-col min-h-0 flex-1">
        {/* Logo */}
        {/* En el celular el menú es un cajón encima de la página: tocar el logo navegaba y lo dejaba abierto,
            tapando la pantalla a la que llevaba (QA del 08/10). Lo cierra, como los demás links. */}
        <Link
          to={HOME}
          aria-label="Ir al inicio"
          onClick={() => onMobileClose?.()}
          className={`
            mb-8 group relative cursor-pointer flex items-center gap-3
            ${effectiveExpanded ? "" : "justify-center"}
          `}
        >
          <div className="absolute inset-0 bg-violet-500/30 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center relative z-10 flex-shrink-0">
            <img
              src={logoPrimary}
              alt={NOMBRE_PRODUCTO}
              className="w-10 h-10 object-contain drop-shadow-md"
            />
          </div>
          {effectiveExpanded && (
            <div className="flex flex-col leading-tight relative z-10 min-w-0 overflow-hidden">
              <span className="text-sm font-bold text-slate-900 truncate">
                {SOLO_ACTIVA ? "DEENEX Activa" : marca?.appName || "Deenex AI"}
              </span>
              <span className="text-[10px] text-slate-400 truncate">
                Marketing IA
              </span>
            </div>
          )}
        </Link>

        {/* El comercio con el que se trabaja, arriba de «WhatsApp»: se cambia desde cualquier pantalla (pedido de Alan del
            11/10/2026). Colapsado, sólo el ícono; el nombre va en el tooltip. Elegir otro recarga la página en ese comercio. */}
        {SOLO_ACTIVA && (
          <div className={`mb-4 w-full ${effectiveExpanded ? "" : "flex justify-center"}`} data-testid="comercio-en-menu">
            <SelectorMarca variante={effectiveExpanded ? "menu" : "menuCompacto"} />
          </div>
        )}

        {/* «Nueva campaña» (DISENO, «Mapa»): debajo del comercio, del equipo. Colapsado, sólo el ícono; el nombre va en el
            title y para el lector de pantalla. Lleva a elegir el tipo (28/10/2026), como el botón de la lista de Campañas:
            un solo punto de entrada. */}
        {SOLO_ACTIVA && equipo && (
          <Link
            to={R_NUEVA}
            data-testid="nueva-campana"
            title="Nueva campaña"
            className={`mb-3 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-violet-600 text-sm font-semibold text-white hover:bg-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 ${effectiveExpanded ? "w-full px-4" : "w-11"}`}
          >
            <Plus size={18} aria-hidden="true" />
            {effectiveExpanded ? <span>Nueva campaña</span> : <span className="sr-only">Nueva campaña</span>}
          </Link>
        )}

        {/* Navigation */}
        <nav
          aria-label="Menú principal"
          className={`flex flex-col gap-0.5 w-full min-h-0 overflow-y-auto overflow-x-hidden ${
            effectiveExpanded ? "" : "items-center"
          }`}
        >
          {nav.map((item, index) => {
            const adentro = estaAdentro(item);
            const abierta = !!item.sub && seccionAbierta(item);
            // la flecha sólo en las secciones de las que NO estás adentro: la tuya va abierta siempre
            const plegable = SOLO_ACTIVA && !!item.sub && !adentro;
            // Lo que no se ve: toda la sección si está cerrada; con el menú colapsado (sin títulos de grupo), los grupos
            // cerrados. Expandido, los avisos de un grupo cerrado van en el título del grupo.
            const gruposCerrados = abierta && item.grupos ? item.grupos.filter((g) => !grupoAbierto(item, g)) : [];
            const ocultos = !abierta ? item.sub || [] : !effectiveExpanded ? gruposCerrados.flatMap((g) => g.sub) : [];
            const avisosTitulo = avisosDe(ocultos);
            const cajaTitulo = SOLO_ACTIVA && !!item.sub && (plegable || avisosTitulo.contestar || avisosTitulo.revisar);
            return (
            <div key={item.id} className="w-full">
              {/* Colapsado no entra: queda para el lector de pantalla, que lo lee antes de las secciones que agrupa. */}
              {item.titulo && (
                <p
                  data-testid={`titulo-${item.id}`}
                  className={effectiveExpanded ? "px-3 pt-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400" : "sr-only"}
                >
                  {item.titulo}
                </p>
              )}
              <div className="relative">
              <NavLink
                to={item.path}
                // Con `activo` propio el NavLink sólo marca aria-current en su ruta exacta: por prefijo, «/activa»
                // (Reactivación) quedaba como página actual también adentro de WhatsApp.
                end={!!item.activo}
                aria-label={item.warning && item.textoAviso ? `${item.label} ${item.textoAviso}` : item.label}
                onClick={() => onMobileClose?.()}
                className={({ isActive }) => `
                  ${effectiveExpanded ? "w-full px-3 gap-3" : "w-11 justify-center"}
                  h-10 rounded-xl flex items-center transition-all duration-200 relative group
                  ${
                    (item.activo ? item.activo(pathname) : isActive)
                      ? "text-indigo-600 bg-indigo-50 shadow-sm"
                      : "text-slate-400 hover:text-indigo-600 hover:bg-slate-50"
                  }
                `}
              >
                {({ isActive: porRuta }) => {
                  const isActive = item.activo ? item.activo(pathname) : porRuta;
                  return (
                  <>
                    <span className="flex-shrink-0">{item.icon}</span>

                    {/* La etiqueta se ve expandido y en el cajón del celular (que va abierto aunque el menú esté contraído). */}
                    {effectiveExpanded ? (
                      <span
                        className={`text-sm font-medium truncate ${
                          isActive
                            ? "text-indigo-600"
                            : "text-slate-600 group-hover:text-indigo-600"
                        }`}
                      >
                        {item.label}
                      </span>
                    ) : (
                      <span className="sr-only">{item.label}</span>
                    )}

                    {/* Warning badge */}
                    {item.warning && (
                      <span
                        className={`w-2 h-2 bg-red-500 rounded-full ring-2 ring-white flex-shrink-0 ${
                          effectiveExpanded ? "ml-auto" : "absolute top-1.5 right-1.5"
                        }`}
                      />
                    )}

                    {/* Active indicator line (collapsed) */}
                    {!effectiveExpanded && isActive && (
                      <div className="absolute left-0 w-1 h-5 bg-indigo-600 rounded-r-full -ml-3" />
                    )}

                    {/* Hover tooltip (collapsed only) */}
                    {!effectiveExpanded && (
                      <span
                        className="pointer-events-none absolute left-full ml-3 px-2.5 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-medium whitespace-nowrap shadow-lg opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all duration-200 z-50"
                        aria-hidden="true"
                      >
                        {item.label}
                        {item.warning && " ⚠️"}
                      </span>
                    )}
                  </>
                  );
                }}
              </NavLink>
              {/* Una sección cerrada: los avisos de sus sub-ítems suben al título (que «3 por contestar» no se esconda
                  por cerrarla) y, con el menú expandido, la flecha para abrirla sin salir de donde estás. Van al lado
                  del link y no adentro: el link tiene aria-label y taparía el aviso para el lector de pantalla. */}
              {cajaTitulo && (
                <div
                  className={
                    effectiveExpanded
                      ? "absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1"
                      : "pointer-events-none absolute top-0 right-0 w-3 h-3"
                  }
                  data-testid={`avisos-${item.id}`}
                >
                  {avisos(avisosTitulo)}
                  {plegable && effectiveExpanded && (
                    <button
                      type="button"
                      onClick={() => alternarSeccion(item.id)}
                      aria-expanded={abierta}
                      aria-controls={`sub-${item.id}`}
                      aria-label={`${abierta ? "Cerrar" : "Abrir"} ${item.label}`}
                      className="w-6 h-6 rounded-md flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-slate-100"
                    >
                      <ChevronDown size={14} className={`transition-transform ${abierta ? "rotate-180" : ""}`} />
                    </button>
                  )}
                </div>
              )}
              </div>

              {/* Sub-navegación de la sección: abierta si estás adentro o si se abrió con la flecha (`seccionAbierta`). */}
              {abierta && (
                <div
                  id={`sub-${item.id}`}
                  className={
                    effectiveExpanded
                      // Sangría justa: «Conversaciones» con su contador no entraba con ml-5/pl-3 y gap-2.5 (se cortaba en
                      // «Conversacio…»). Con ml-4/pl-2.5 y gap-2 entra entero a 220 px.
                      ? "mt-1 mb-1 ml-4 pl-2.5 border-l border-slate-200 flex flex-col gap-0.5"
                      : "mt-1 mb-1 flex flex-col items-center gap-1"
                  }
                >
                  {item.grupos
                    ? item.grupos.map((g) => {
                        const gAbierto = grupoAbierto(item, g);
                        const gPlegable = !g.activo(pathname);
                        // colapsado no hay títulos de grupo: sólo los íconos de los grupos abiertos
                        if (!effectiveExpanded) {
                          return gAbierto ? (
                            <div key={g.id} className="flex flex-col items-center gap-1">
                              {g.sub.map(subItem)}
                            </div>
                          ) : null;
                        }
                        return (
                          <div key={g.id} data-testid={`grupo-${item.id}-${g.id}`}>
                            <div className="flex items-center justify-between gap-1 h-7 pl-2 pr-0.5 mt-1">
                              <span className={`text-[11px] font-semibold uppercase tracking-wider truncate ${g.activo(pathname) ? "text-indigo-500" : "text-slate-400"}`}>
                                {g.label}
                              </span>
                              <span className="flex items-center gap-1" data-testid={`avisos-${item.id}-${g.id}`}>
                                {!gAbierto && avisos(avisosDe(g.sub))}
                                {gPlegable && (
                                  <button
                                    type="button"
                                    onClick={() => alternarSeccion(claveGrupo(item, g))}
                                    aria-expanded={gAbierto}
                                    aria-controls={`sub-${item.id}-${g.id}`}
                                    aria-label={`${gAbierto ? "Cerrar" : "Abrir"} ${g.label}`}
                                    className="w-6 h-6 rounded-md flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-slate-100"
                                  >
                                    <ChevronDown size={14} className={`transition-transform ${gAbierto ? "rotate-180" : ""}`} />
                                  </button>
                                )}
                              </span>
                            </div>
                            {gAbierto && (
                              <div id={`sub-${item.id}-${g.id}`} className="flex flex-col gap-0.5">
                                {g.sub.map(subItem)}
                              </div>
                            )}
                          </div>
                        );
                      })
                    : (item.sub || []).map(subItem)}
                </div>
              )}

              {/* Separador entre secciones (en el menú completo, después de Dashboard + Campañas). En Activa separan los
                  rótulos CANALES y TU MARCA: una raya debajo de cada ítem era ruido (auditoría de diseño del 18/10/2026). */}
              {!SOLO_ACTIVA && index === 1 && (
                <div className="my-2 mx-auto w-6 h-px bg-slate-200 rounded-full" />
              )}
            </div>
            );
          })}
        </nav>
      </div>

      {/* ── Bottom Section ──
          Una sola fila: el comercio con el rol de quien opera, y Accesos y Cerrar sesión como íconos con su nombre al
          pasar el mouse. Pedido de Alan del 12/10/2026 («que ocupe prácticamente poco espacio»): eran un separador, la
          tarjeta del comercio y dos botones de 40 px (~140 px) que le quitaban lugar a la lista de secciones; ahora ~40.
          Colapsado, lo mismo en una columna angosta. */}
      <div
        className={`flex-shrink-0 border-t border-slate-100 pt-2 mt-1 ${
          effectiveExpanded ? "flex items-center gap-1 px-1" : "flex flex-col items-center gap-1"
        }`}
      >
        {/* El nombre del comercio también está arriba, en el selector: acá va con el rol de quien opera. */}
        <div
          data-testid="identidad-menu"
          title={`${comercio ?? (marca?.appName || "Mi Marca")} · ${comercio === null ? "Administrador" : rol === "comercio" ? "Tu marca" : "Operador"}`}
          className={`flex items-center gap-2 min-w-0 ${effectiveExpanded ? "flex-1 pl-1" : "justify-center"}`}
        >
          <div className="relative flex-shrink-0">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-700 flex items-center justify-center text-[9px] font-bold text-white shadow-sm overflow-hidden">
              {comercio !== null ? (
                <span>{iniciales(comercio)}</span>
              ) : marca?.appLogo || (marca as any)?.images?.logo ? (
                <img
                  src={marca?.appLogo || (marca as any)?.images?.logo}
                  alt={marca?.appName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span>GJ</span>
              )}
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full border border-white" />
          </div>
          {effectiveExpanded && (
            <div className="flex flex-col min-w-0 leading-tight">
              <span className="text-xs font-semibold text-slate-800 truncate">
                {comercio ?? (marca?.appName || "Mi Marca")}
              </span>
              <span className="text-[10px] text-slate-400 truncate">
                {comercio === null ? "Administrador" : rol === "comercio" ? "Tu marca" : "Operador"}
              </span>
            </div>
          )}
        </div>

        {/* Settings: la pantalla es un mock entero (API key falsa incluida). Con SOLO_ACTIVA no se
            ofrece; su ruta además redirige al inicio (SoloActiva en App.tsx). */}
        {!SOLO_ACTIVA && (
          <NavLink
            to="/settings"
            aria-label="Configuración"
            title="Configuración"
            className={({ isActive }) => `${CLASE_ICONO_PIE} ${isActive ? "text-indigo-600 bg-indigo-50" : "text-slate-400 hover:text-indigo-600 hover:bg-slate-50"}`}
          >
            <Settings size={16} />
            <BurbujaPie texto="Configuración" arriba={effectiveExpanded} />
          </NavLink>
        )}

        {/* Accesos (quién puede entrar por mail): no es de ningún canal, así que va acá y no adentro de una sección.
            Sólo con una sesión de persona (su `user` trae `acceso`) y del equipo; una sesión guardada antes de Accesos no lo trae
            y el link aparece en el próximo login. En el menú completo sigue adentro de «DEENEX Activa». */}
        {SOLO_ACTIVA && !!user?.acceso && equipo && (
          <NavLink
            to="/activa/accesos"
            aria-label="Accesos"
            title="Quién puede entrar"
            onClick={() => onMobileClose?.()}
            className={({ isActive }) => `${CLASE_ICONO_PIE} ${isActive ? "text-indigo-600 bg-indigo-50" : "text-slate-400 hover:text-indigo-600 hover:bg-slate-50"}`}
          >
            <UserPlus size={16} />
            <BurbujaPie texto="Accesos" arriba={effectiveExpanded} />
          </NavLink>
        )}

        {/* Logout */}
        <button
          onClick={handleLogout}
          disabled={loggingOut}
          aria-label="Cerrar sesión"
          title="Cerrar sesión"
          className={`${CLASE_ICONO_PIE} text-slate-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <LogOut size={16} className={loggingOut ? "animate-pulse" : ""} />
          <BurbujaPie texto="Cerrar sesión" arriba={effectiveExpanded} />
        </button>
      </div>
    </aside>

    {/* ── Pestaña contraer/expandir ──
        Vive FUERA del <aside> a propósito: el aside tiene `overflow-hidden` (para que los labels
        no se desborden al colapsar), así que un botón posicionado sobre su borde quedaba RECORTADO
        por la mitad. Acá va `fixed` y se desplaza junto con el ancho del sidebar (220/68px) con la
        misma transición, así queda pegado al borde sin cortarse.
        Va centrado verticalmente y no arriba: con el sidebar colapsado el contenido se corre hacia
        la izquierda y una pestaña alta se montaba encima del título de la página.
        Sólo desktop: en mobile el drawer se cierra tocando el overlay, no con esta pestaña. */}
    <button
      onClick={toggle}
      aria-label={effectiveExpanded ? "Contraer menú" : "Expandir menú"}
      title={effectiveExpanded ? "Contraer menú" : "Expandir menú"}
      className={`
        hidden md:flex fixed top-1/2 -translate-y-1/2 z-30 items-center justify-center
        w-[22px] h-11 rounded-r-lg
        border border-l-0 border-slate-200/70 bg-white
        text-slate-400 hover:text-violet-600 hover:bg-violet-50
        shadow-[2px_0_8px_rgba(15,23,42,0.06)]
        focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500
        transition-[left,color,background-color] duration-300 ease-in-out
        ${effectiveExpanded ? "left-[220px]" : "left-[68px]"}
      `}
    >
      {effectiveExpanded ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
    </button>
    </>
  );
};

/** Un ícono del pie del menú (Accesos, Cerrar sesión): chico, para que el pie ocupe una sola fila. */
/** Al pasar el mouse por el punto violeta de una sección: el lector de pantalla lee el mismo aviso en el `sr-only`. */
const TITULO_REVISAR = "Hay algo para revisar en Configuración";

const CLASE_ICONO_PIE = "group relative flex items-center justify-center w-8 h-8 flex-shrink-0 rounded-lg transition-colors duration-200";

/**
 * El nombre de un ícono del pie al pasar el mouse. Con el menú abierto va arriba (el pie está al fondo de la pantalla
 * y a la derecha no hay lugar); cerrado, a la derecha, como los demás ítems del menú.
 */
function BurbujaPie({ texto, arriba }: { texto: string; arriba: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`pointer-events-none absolute z-50 px-2.5 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-medium whitespace-nowrap shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${
        arriba ? "bottom-full mb-2 right-0" : "left-full ml-3"
      }`}
    >
      {texto}
    </span>
  );
}

export default Sidebar;
